import { Group, RawGroup } from '@/store/types';

export const handleize = (str: string): string => {
	const handle = str
		?.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/-$/, '')
		.replace(/^-/, '');
	return handle ?? '';
};

const getParentPath = (path: string): string => path.split('/').slice(0, -2).join('/') + '/';

/**
 * Get the groups that doesn't have parent
 */
export const getRootGroups = (groups: RawGroup[]): RawGroup[] => {
	const paths = new Set(groups.map(({ path }) => path));
	return groups.filter(({ path }) => !paths.has(getParentPath(path)));
};

/**
 * Get groups with tree structure from the RawGroup array
 *
 * Builds a path -> children index in a single pass so this runs in O(n) instead of
 * re-scanning the whole `groups` array for every node in the tree.
 */
export const getGroupTree = (groups: RawGroup[], level = 1, parent?: RawGroup): Group[] => {
	const childrenByParentPath = new Map<string, RawGroup[]>();
	groups.forEach((group) => {
		const parentPath = getParentPath(group.path);
		const siblings = childrenByParentPath.get(parentPath);
		if (siblings) {
			siblings.push(group);
		} else {
			childrenByParentPath.set(parentPath, [group]);
		}
	});

	const buildNode = (rawGroup: RawGroup, nodeLevel: number): Group => ({
		...rawGroup,
		level: nodeLevel,
		children: (childrenByParentPath.get(rawGroup.path) ?? []).map((child) => buildNode(child, nodeLevel + 1)),
	});

	const startingGroups = parent ? childrenByParentPath.get(parent.path) ?? [] : getRootGroups(groups);

	return startingGroups.map((rawGroup) => buildNode(rawGroup, level));
};

/**
 * Get groups array from the groups tree
 */
export const getGroupArray = (source: Group[]): Group[] => {
	const result: Group[] = [];
	source.forEach((group) => {
		result.push(group, ...getGroupArray(group.children));
	});
	return result;
};

/**
 * getAncestorGroups
 * @param groups array of all known groups
 * @param group group of interest, to search for parents
 * @param ancestors ancestor groups accumulator of supplied group
 * @returns array of the current group and any ancestor groups, in order
 */
export function getGroupAncestry(groups: Group[], group: Group, ancestors: Group[] = []): Group[] {
	ancestors.push(group);
	const parent = groups.find((g) => g.children.some((child) => child.id === group.id));
	if (parent) {
		getGroupAncestry(groups, parent, ancestors);
	}
	return ancestors;
}
